import { Clock } from 'lucide-react'
import type { IndexCompetition, IndexHomeData } from './types'
import { IndexRow, IndexTile } from './IndexRow'
import { followLink, itemKey } from './view'
import styles from './IndexSearch.module.css'

export interface IndexHomeProps {
  home: IndexHomeData
  idPrefix: string
  activeKey: string | null
  onNavigate: (href: string) => void
  onHover: (key: string) => void
  onRecentSelect: (query: string) => void
  onClearRecent?: () => void
}

/** What the Index shows before anything is typed. */
export function IndexHome({ home, idPrefix, activeKey, onNavigate, onHover, onRecentSelect, onClearRecent }: IndexHomeProps) {
  const section = (id: 'live' | 'open', label: string, items: IndexCompetition[]) =>
    items.length > 0 && (
      <section aria-label={label} className={styles.group}>
        <h2 className={styles.heading}>{label}</h2>
        <div className={styles.panel}>
          {items.map((item) => {
            const key = itemKey(id, item)
            return (
              <IndexRow
                key={key}
                id={`${idPrefix}-${key}`}
                item={item}
                query=""
                active={activeKey === key}
                onNavigate={onNavigate}
                onHover={() => onHover(key)}
              />
            )
          })}
        </div>
      </section>
    )

  const nothing = home.live.length + home.open.length + home.recent.length + home.spaces.length === 0
  if (nothing) {
    return (
      <div className={styles.empty}>
        <p className={styles.emptyTitle}>Find anyone, any team, any league</p>
        <p className={styles.rowSub}>Search players, teams, clubs, leagues and competitions across Carousel.</p>
      </div>
    )
  }

  return (
    <div className={styles.home}>
      <div className={styles.stack}>
        {section('live', 'Happening now', home.live)}
        {section('open', 'Open for registration', home.open)}
      </div>
      <div className={styles.stack}>
        {home.recent.length > 0 && (
          <section aria-label="Recent searches" className={styles.group}>
            <div className={styles.sectionHead}>
              <h2 className={styles.heading}>Recent</h2>
              {onClearRecent && (
                <button type="button" className={styles.seeAll} onClick={onClearRecent}>
                  Clear
                </button>
              )}
            </div>
            {home.recent.map((q) => (
              <button key={q} type="button" className={styles.recent} onClick={() => onRecentSelect(q)}>
                <Clock size={15} aria-hidden="true" />
                <span>{q}</span>
              </button>
            ))}
          </section>
        )}
        {home.spaces.length > 0 && (
          <section aria-label="Your spaces" className={styles.group}>
            <h2 className={styles.heading}>Your spaces</h2>
            {home.spaces.map((space) => (
              <a
                key={space.id}
                href={space.href}
                className={`${styles.row} ${styles.rowCompact}`}
                onClick={(e) => followLink(e, space.href, onNavigate)}
              >
                <IndexTile name={space.name} logoUrl={space.logoUrl} color={space.color} size="sm" />
                <span className={styles.rowText}>
                  <span className={styles.rowName}>{space.name}</span>
                </span>
                <span className={styles.rowSub}>
                  {space.roleLabel ? `${space.kindLabel} · ${space.roleLabel}` : space.kindLabel}
                </span>
              </a>
            ))}
          </section>
        )}
      </div>
    </div>
  )
}
